import jsPDF from "jspdf";

/**
 * Export utilities untuk PDF dan CSV
 */

/**
 * Export data to CSV
 */
export function exportToCSV(
  data: Array<Record<string, unknown>>,
  filename: string = "export.csv"
): void {
  if (data.length === 0) {
    alert("No data to export");
    return;
  }

  const firstRow = data[0];
  if (!firstRow) {
    alert("No data to export");
    return;
  }

  // Get headers from first object
  const headers = Object.keys(firstRow);
  
  // Create CSV content
  const csvContent = [
    // Headers
    headers.join(","),
    // Data rows
    ...data.map((row) =>
      headers
        .map((header) => {
          const value = row[header];
          // Handle values that might contain commas or quotes
          if (value === null || value === undefined) {
            return "";
          }
          const stringValue = String(value);
          // Escape quotes and wrap in quotes if contains comma or quote
          if (stringValue.includes(",") || stringValue.includes('"') || stringValue.includes("\n")) {
            return `"${stringValue.replace(/"/g, '""')}"`;
          }
          return stringValue;
        })
        .join(",")
    ),
  ].join("\n");

  // Create blob and download
  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const link = document.createElement("a");
  const url = URL.createObjectURL(blob);
  link.setAttribute("href", url);
  link.setAttribute("download", filename);
  link.style.visibility = "hidden";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

/**
 * Export data to PDF
 */
export function exportToPDF(
  title: string,
  data: Array<Record<string, unknown>>,
  columns: Array<{ key: string; label: string; width?: number }>,
  filename: string = "export.pdf"
): void {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 20;
  const startY = margin + 20;
  let currentY = startY;

  // Add title
  doc.setFontSize(16);
  doc.setFont("helvetica", "bold");
  doc.text(title, margin, currentY);
  currentY += 10;

  // Add date
  doc.setFontSize(10);
  doc.setFont("helvetica", "normal");
  doc.text(`Generated: ${new Date().toLocaleString()}`, margin, currentY);
  currentY += 15;

  // Calculate column widths
  const availableWidth = pageWidth - 2 * margin;
  const totalWidth = columns.reduce((sum, col) => sum + (col.width || 1), 0);
  const columnWidths = columns.map((col) => ((col.width || 1) / totalWidth) * availableWidth);
  // Safe indexed access (noUncheckedIndexedAccess)
  const widthAt = (index: number): number => columnWidths[index] ?? 0;

  // Add table headers
  doc.setFontSize(10);
  doc.setFont("helvetica", "bold");
  let xPos = margin;
  columns.forEach((col, index) => {
    doc.text(col.label, xPos, currentY);
    xPos += widthAt(index);
  });
  currentY += 7;

  // Add horizontal line
  doc.setLineWidth(0.5);
  doc.line(margin, currentY - 3, pageWidth - margin, currentY - 3);
  currentY += 3;

  // Add data rows
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  
  data.forEach((row) => {
    // Check if we need a new page
    if (currentY > pageHeight - 30) {
      doc.addPage();
      currentY = margin + 10;
      
      // Redraw headers on new page
      doc.setFont("helvetica", "bold");
      xPos = margin;
      columns.forEach((col, index) => {
        doc.text(col.label, xPos, currentY);
        xPos += widthAt(index);
      });
      currentY += 7;
      doc.line(margin, currentY - 3, pageWidth - margin, currentY - 3);
      currentY += 3;
      doc.setFont("helvetica", "normal");
    }

    xPos = margin;
    columns.forEach((col, index) => {
      const value = row[col.key];
      const text = value !== null && value !== undefined ? String(value) : "";
      // Truncate if too long
      const maxWidth = widthAt(index) - 2;
      const truncatedText = doc.splitTextToSize(text, maxWidth);
      doc.text(truncatedText, xPos, currentY);
      xPos += widthAt(index);
    });
    currentY += 7;
  });

  // Save PDF
  doc.save(filename);
}

/**
 * Export monthly report to PDF
 */
export function exportMonthlyReportToPDF(
  report: {
    period: string;
    income: {
      total: number;
      count?: number;
      average?: number;
      bySource: Array<{ sourceName: string; total: number; count: number }>;
    };
    expenses: {
      total: number;
      count?: number;
      average?: number;
      byCategory: Array<{ category: string; total: number; count: number }>;
    };
    savings: {
      amount: number;
      rate: number;
    };
  },
  filename?: string
): void {
  const doc = new jsPDF();
  const margin = 20;
  let currentY = margin;

  // Title
  doc.setFontSize(18);
  doc.setFont("helvetica", "bold");
  doc.text("Monthly Budget Report", margin, currentY);
  currentY += 10;

  // Period
  doc.setFontSize(12);
  doc.setFont("helvetica", "normal");
  doc.text(`Period: ${report.period}`, margin, currentY);
  currentY += 15;

  // Summary
  doc.setFontSize(14);
  doc.setFont("helvetica", "bold");
  doc.text("Summary", margin, currentY);
  currentY += 10;

  doc.setFontSize(10);
  doc.setFont("helvetica", "normal");
  doc.text(`Total Income: $${report.income.total.toFixed(2)}`, margin, currentY);
  currentY += 7;
  doc.text(`Total Expenses: $${report.expenses.total.toFixed(2)}`, margin, currentY);
  currentY += 7;
  doc.text(`Savings: $${report.savings.amount.toFixed(2)}`, margin, currentY);
  currentY += 7;
  doc.text(`Savings Rate: ${report.savings.rate.toFixed(1)}%`, margin, currentY);
  currentY += 15;

  // Income by Source
  if (report.income.bySource.length > 0) {
    doc.setFontSize(14);
    doc.setFont("helvetica", "bold");
    doc.text("Income by Source", margin, currentY);
    currentY += 10;

    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    report.income.bySource.forEach((source) => {
      doc.text(
        `${source.sourceName}: $${source.total.toFixed(2)} (${source.count} records)`,
        margin + 5,
        currentY
      );
      currentY += 7;
    });
    currentY += 5;
  }

  // Expenses by Category
  if (report.expenses.byCategory.length > 0) {
    if (currentY > 250) {
      doc.addPage();
      currentY = margin;
    }

    doc.setFontSize(14);
    doc.setFont("helvetica", "bold");
    doc.text("Expenses by Category", margin, currentY);
    currentY += 10;

    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    report.expenses.byCategory.forEach((category) => {
      doc.text(
        `${category.category}: $${category.total.toFixed(2)} (${category.count} records)`,
        margin + 5,
        currentY
      );
      currentY += 7;
    });
  }

  // Footer
  const pageHeight = doc.internal.pageSize.getHeight();
  doc.setFontSize(8);
  doc.setFont("helvetica", "italic");
  doc.text(
    `Generated on ${new Date().toLocaleString()}`,
    margin,
    pageHeight - 10
  );

  doc.save(filename || `monthly-report-${report.period.replace(/\s+/g, "-")}.pdf`);
}

/**
 * Export income-expense report to CSV
 */
export function exportIncomeExpenseToCSV(
  report: {
    period: {
      start: string;
      end: string;
    };
    income: {
      total: number;
      bySource: Array<{ sourceName: string; total: number; count: number }>;
    };
    expenses: {
      total: number;
      byCategory: Array<{ category: string; total: number; count: number }>;
    };
    comparison: {
      difference: number;
      savingsRate: number;
    };
  },
  filename?: string
): void {
  const data: Array<Record<string, unknown>> = [
    { Type: "Income", Category: "Total", Amount: report.income.total, Count: "" },
    ...report.income.bySource.map((source) => ({
      Type: "Income",
      Category: source.sourceName,
      Amount: source.total,
      Count: source.count,
    })),
    { Type: "Expense", Category: "Total", Amount: report.expenses.total, Count: "" },
    ...report.expenses.byCategory.map((category) => ({
      Type: "Expense",
      Category: category.category,
      Amount: category.total,
      Count: category.count,
    })),
    { Type: "Summary", Category: "Savings", Amount: report.comparison.difference, Count: "" },
    { Type: "Summary", Category: "Savings Rate (%)", Amount: report.comparison.savingsRate, Count: "" },
  ];

  exportToCSV(data, filename || `income-expense-${report.period.start}-${report.period.end}.csv`);
}

/**
 * Export category breakdown to CSV
 */
export function exportCategoryBreakdownToCSV(
  report: {
    period: {
      start: string;
      end: string;
    };
    incomeBySource: Array<{
      sourceName: string;
      total: number;
      count: number;
      percentage: number;
    }>;
    expensesByCategory: Array<{
      category: string;
      total: number;
      count: number;
      percentage: number;
    }>;
    totals: {
      income: number;
      expenses: number;
      savings: number;
    };
  },
  filename?: string
): void {
  const data: Array<Record<string, unknown>> = [
    { Type: "Income", Category: "Total", Amount: report.totals.income, Count: "", Percentage: "100%" },
    ...report.incomeBySource.map((source) => ({
      Type: "Income",
      Category: source.sourceName,
      Amount: source.total,
      Count: source.count,
      Percentage: `${source.percentage.toFixed(1)}%`,
    })),
    { Type: "Expense", Category: "Total", Amount: report.totals.expenses, Count: "", Percentage: "100%" },
    ...report.expensesByCategory.map((category) => ({
      Type: "Expense",
      Category: category.category,
      Amount: category.total,
      Count: category.count,
      Percentage: `${category.percentage.toFixed(1)}%`,
    })),
    { Type: "Summary", Category: "Savings", Amount: report.totals.savings, Count: "", Percentage: "" },
  ];

  exportToCSV(data, filename || `category-breakdown-${report.period.start}-${report.period.end}.csv`);
}

/**
 * Export net worth report to PDF
 */
export function exportNetWorthReportToPDF(
  report: {
    current: {
      netWorth: number;
      totalAssets: number;
      totalDebts: number;
      date: string;
    };
    change?: {
      netWorth: number;
      percentage: number;
      assets: number;
      debts: number;
    };
  },
  filename?: string
): void {
  const doc = new jsPDF();
  const margin = 20;
  let currentY = margin;

  // Title
  doc.setFontSize(18);
  doc.setFont("helvetica", "bold");
  doc.text("Net Worth Report", margin, currentY);
  currentY += 10;

  // Date
  doc.setFontSize(10);
  doc.setFont("helvetica", "normal");
  doc.text(`Date: ${new Date(report.current.date).toLocaleDateString()}`, margin, currentY);
  currentY += 15;

  // Current Net Worth
  doc.setFontSize(14);
  doc.setFont("helvetica", "bold");
  doc.text("Current Net Worth", margin, currentY);
  currentY += 10;

  doc.setFontSize(12);
  doc.setFont("helvetica", "normal");
  doc.text(`Total Assets: $${report.current.totalAssets.toFixed(2)}`, margin + 5, currentY);
  currentY += 7;
  doc.text(`Total Debts: $${report.current.totalDebts.toFixed(2)}`, margin + 5, currentY);
  currentY += 7;
  doc.setFont("helvetica", "bold");
  doc.text(`Net Worth: $${report.current.netWorth.toFixed(2)}`, margin + 5, currentY);
  currentY += 15;

  // Change (if available)
  if (report.change) {
    doc.setFontSize(14);
    doc.setFont("helvetica", "bold");
    doc.text("Change Analysis", margin, currentY);
    currentY += 10;

    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    doc.text(
      `Net Worth Change: $${report.change.netWorth.toFixed(2)} (${report.change.percentage.toFixed(1)}%)`,
      margin + 5,
      currentY
    );
    currentY += 7;
    doc.text(`Assets Change: $${report.change.assets.toFixed(2)}`, margin + 5, currentY);
    currentY += 7;
    doc.text(`Debts Change: $${report.change.debts.toFixed(2)}`, margin + 5, currentY);
  }

  // Footer
  const pageHeight = doc.internal.pageSize.getHeight();
  doc.setFontSize(8);
  doc.setFont("helvetica", "italic");
  doc.text(
    `Generated on ${new Date().toLocaleString()}`,
    margin,
    pageHeight - 10
  );

  doc.save(filename || `net-worth-report-${new Date().toISOString().split("T")[0]}.pdf`);
}

